import type { CalculationType, ServiceFee } from "@lib/zod/serviceFee";
import { DEFAULT_PLATFORM_SERVICE_FEE } from "../platformServiceFee";
import { exclusiveRate, inclusiveRate } from "./rateCalc";
import type { calculateSubTotal } from "./calculateSubTotal";
import { type TaxSettings } from "../zod/taxSchema"
import { formatCurrency } from "./formatCurrency";
import type { CreditPromoSettingsType } from "@lib/zod/CreditPromoSettingsSchema"
import type { FirstOrdersPromoSettingsType } from "@lib/zod/FirstOrdersPromoSettingsSchema"
import { fixMath } from "./fixMath";
import { getRoundCount, roundDecimalPlaces } from "./formatAmountForStripe";


export function calculateCartBreakdown(
    subTotal: ReturnType<typeof calculateSubTotal>,
    platformServiceFee: ServiceFee | null,
    supportPartnerServiceFee: ServiceFee | null,
    marketingPartnerServiceFee: ServiceFee | null,
    taxSettings: TaxSettings | undefined,
    // storeBillingAddressSalesTaxRate: number | null | undefined,
    tolerableFeeByStoreCalculationType: CalculationType,
    tolerableRateByStoreToCoverServiceFees: number,
    tolerableFeePerOrderByStoreToCoverServiceFees: number,
    isOfferDiscountEnabled: boolean,
    usedCredit: number,
    creditPromoSettings: CreditPromoSettingsType | undefined,
    firstOrdersPromoSettings: FirstOrdersPromoSettingsType | undefined,
    storeUserOrderCount: number,
) {
    const roundCount = getRoundCount(taxSettings?.currency ?? 'gbp')

    // const platformFeeAmount = platformServiceFee
    //     ? fixMath.add(
    //         exclusiveRate(subTotal.totalWithTax, platformServiceFee.ratePerOrder ?? 0),
    //         platformServiceFee.feePerOrder ?? 0
    //     )
    //     : 0;

    const platformFeeAmount = supportPartnerServiceFee
        ? getLimitedPartnerFeeAmount(
            subTotal.totalWithTax,
            platformServiceFee ?? DEFAULT_PLATFORM_SERVICE_FEE,
            MAX_PLATFORM_PARTNER_FEE_RATE
        )
        : 0;

    const supportFeeAmount = supportPartnerServiceFee
        ? getLimitedPartnerFeeAmount(
            subTotal.totalWithTax,
            supportPartnerServiceFee,
            MAX_SUPPORT_PARTNER_FEE_RATE
        )
        : 0;
    const marketingFeeAmount = marketingPartnerServiceFee
        ? getLimitedPartnerFeeAmount(
            subTotal.totalWithTax,
            marketingPartnerServiceFee,
            MAX_MARKETING_PARTNER_FEE_RATE
        )
        : 0;

    const combinedPlatformFeeAmountIncludingTax = fixMath.add(
        fixMath.add(platformFeeAmount, supportFeeAmount),
        marketingFeeAmount
    );

    const combinedPlatformFeeTaxonly = inclusiveRate(combinedPlatformFeeAmountIncludingTax, (taxSettings?.taxRateForServiceFees ?? 0));

    const combinedPlatformFeeWithoutTax = fixMath.subtract(combinedPlatformFeeAmountIncludingTax, combinedPlatformFeeTaxonly);

    type PlatformFeeBreakdown = {
        totalWithTax: number; // always include tax
        itemTotal: number; // always exclude tax
        tax: number; // the tax
    }
    const platformFeeBreakdown: Partial<Record<"platform" | "support" | "marketing", PlatformFeeBreakdown>> = {};

    if (platformServiceFee && platformFeeAmount > 0) {
        const feeTax = inclusiveRate(platformFeeAmount, (taxSettings?.taxRateForServiceFees ?? 0));
        platformFeeBreakdown.platform = {
            totalWithTax: platformFeeAmount,
            itemTotal: fixMath.subtract(platformFeeAmount, feeTax),
            tax: feeTax,
        };
    }

    if (supportPartnerServiceFee && supportFeeAmount > 0) {
        const feeTax = inclusiveRate(supportFeeAmount, (taxSettings?.taxRateForServiceFees ?? 0));
        platformFeeBreakdown.support = {
            totalWithTax: supportFeeAmount,
            itemTotal: fixMath.subtract(supportFeeAmount, feeTax),
            tax: feeTax,
        }
    }

    if (marketingPartnerServiceFee && marketingFeeAmount > 0) {
        const feeTax = inclusiveRate(marketingFeeAmount, (taxSettings?.taxRateForServiceFees ?? 0));
        platformFeeBreakdown.marketing = {
            totalWithTax: marketingFeeAmount,
            itemTotal: fixMath.subtract(marketingFeeAmount, feeTax),
            tax: feeTax,
        };
    }



    const tolerableAmountByStore = tolerableFeeByStoreCalculationType === "INCLUDE"
        ? Math.max(
            exclusiveRate(subTotal.totalWithTax, tolerableRateByStoreToCoverServiceFees || 0),
            tolerableFeePerOrderByStoreToCoverServiceFees || 0
        )
        : 0;

    let buyerPaysPlatformFee = {
        totalWithTax: 0,
        itemTotal: 0,
        tax: 0,
        shownFee: 0
    };

    if (combinedPlatformFeeAmountIncludingTax > tolerableAmountByStore) {
        const extraAmount = fixMath.subtract(combinedPlatformFeeAmountIncludingTax, tolerableAmountByStore);
        const extraTax = inclusiveRate(extraAmount, (taxSettings?.taxRateForServiceFees ?? 0));
        const shownFee = taxSettings?.salesTaxType === 'APPLY_TAX_ON_TOP_OF_PRICES' ?
            fixMath.subtract(extraAmount, extraTax) :
            extraAmount

        buyerPaysPlatformFee = {
            totalWithTax: extraAmount,
            itemTotal: fixMath.subtract(extraAmount, extraTax),
            tax: extraTax,
            shownFee: shownFee
        };
    }


    let discount = 0;
    if (isOfferDiscountEnabled && tolerableAmountByStore > combinedPlatformFeeAmountIncludingTax) {
        discount = fixMath.subtract(tolerableAmountByStore, combinedPlatformFeeAmountIncludingTax);
        // if (taxSettings.salesTaxType === 'APPLY_TAX_ON_TOP_OF_PRICES') {
        discount = fixMath.subtract(discount, inclusiveRate(discount, (taxSettings?.taxRateForServiceFees ?? 0)));
        // }
    }



    const totalTax = fixMath.add(subTotal.tax, buyerPaysPlatformFee.tax);
    let buyerTotal = fixMath.subtract(
        fixMath.add(subTotal.totalWithTax, buyerPaysPlatformFee.totalWithTax),
        discount
    );
    // First Orders Promo Discount
    let firstOrdersDiscount = 0;
    if (
        firstOrdersPromoSettings &&
        firstOrdersPromoSettings.isFirstOrdersPromoEnabled &&
        storeUserOrderCount < firstOrdersPromoSettings.orderCountNumber &&
        subTotal.shownFee >= firstOrdersPromoSettings.minSubtotalAmount &&
        firstOrdersPromoSettings.discountAmount > 0
    ) {
        firstOrdersDiscount = firstOrdersPromoSettings.discountAmount;
        buyerTotal = fixMath.subtract(buyerTotal, firstOrdersDiscount);
    }

    // Never apply discount to tax
    // I think, discount should be simple and stupid. 
    // I can not deal with the logic of applying discount to tax or individual fees in the cart
    // it is too complex for my stupid brain to handle
    if (totalTax > buyerTotal) {
        buyerTotal = totalTax;
    }

    if (usedCredit < 0) {
        usedCredit = 0; // prevent negative usedCredit
    }
    if (usedCredit > buyerTotal) {
        usedCredit = buyerTotal; // can not be more than buyerTotal
    }
    const buyerPaysAfterCreditPromo = fixMath.subtract(buyerTotal, usedCredit);


    // let earnCreditAmountByOrdering = 0;
    // if (
    //     creditPromoSettings &&
    //     creditPromoSettings.isCreditPromoEnabled &&
    //     creditPromoSettings.earnRatePerOrder > 0 &&
    //     (!usedCredit || usedCredit === 0) // to prevent earning and using at the same time
    // ) {
    //     earnCreditAmountByOrdering = exclusiveRate(fixMath.subtract(subTotal.totalWithTax, totalTax), creditPromoSettings.earnRatePerOrder);
    // }
    let earnCreditAmountByOrdering = 0;
    if (
        creditPromoSettings &&
        creditPromoSettings.isCreditPromoEnabled &&
        creditPromoSettings.earnRatePerOrder > 0
    ) {
        earnCreditAmountByOrdering = exclusiveRate(
            // can not earn rate on used credit amount, so we need to subtract it from the subTotal.itemTotal
            fixMath.subtract(subTotal.itemTotal, usedCredit ?? 0),
            creditPromoSettings.earnRatePerOrder
        );
    }

    const allTransparencyBreakdown: {
        type: keyof typeof feeBreakdownNameMap,
        currencyShownFee: string, // formatted currency fee
        // note: string,
    }[] = [];

    if (totalTax > 0) {
        allTransparencyBreakdown.push({
            type: 'tax',
            currencyShownFee: formatCurrency(totalTax, taxSettings?.currency),

        });
    }
    Object.entries(platformFeeBreakdown).forEach(([_key, fee]) => {
        allTransparencyBreakdown.push({
            type: _key as 'platform' | 'support' | 'marketing',
            currencyShownFee: formatCurrency(fee.itemTotal, taxSettings?.currency),
        });
    });
    const netStoreRevenue = fixMath.subtract(
        fixMath.subtract(buyerTotal, combinedPlatformFeeWithoutTax),
        totalTax
    );
    allTransparencyBreakdown.push({
        type: 'seller',
        currencyShownFee: formatCurrency(netStoreRevenue, taxSettings?.currency),
    });

    let buyerPaysTaxAndFeesShownFee = taxSettings?.salesTaxType === 'APPLY_TAX_ON_TOP_OF_PRICES' ?
        fixMath.add(buyerPaysPlatformFee.shownFee, totalTax) :
        buyerPaysPlatformFee.shownFee;

    const buyerPaysTaxAndFees: {
        type: keyof typeof feeBreakdownNameMap,
        currencyShownFee: string, // formatted currency fee
    }[] = [];
    if (totalTax > 0 && taxSettings?.salesTaxType === 'APPLY_TAX_ON_TOP_OF_PRICES') {
        buyerPaysTaxAndFees.push({
            type: `tax`,
            currencyShownFee: formatCurrency(totalTax, taxSettings?.currency),
        });
    }

    if (buyerPaysPlatformFee.shownFee > 0) {
        buyerPaysTaxAndFees.push({
            type: `serviceFee`,
            currencyShownFee: formatCurrency(buyerPaysPlatformFee.shownFee, taxSettings?.currency),
        });
    }


    const buyerPaysTaxAndFeesName = taxSettings?.salesTaxType === 'APPLY_TAX_ON_TOP_OF_PRICES' ?
        `Taxes${(buyerPaysPlatformFee.shownFee > 0) ? ' & Service Fees' : ''}` :
        'Platform support fee';


    Object.entries(platformFeeBreakdown).forEach(([_key, fee]) => {
        platformFeeBreakdown[_key as keyof typeof platformFeeBreakdown] = {
            ...fee,
            totalWithTax: roundDecimalPlaces(fee.totalWithTax, roundCount),
            itemTotal: roundDecimalPlaces(fee.itemTotal, roundCount),
            tax: roundDecimalPlaces(fee.tax, roundCount),
        }
    });

    const applicationFeeWithTax = fixMath.add(
        fixMath.add(
            platformFeeBreakdown.platform?.totalWithTax ?? 0,
            platformFeeBreakdown.support?.totalWithTax ?? 0
        ),
        platformFeeBreakdown.marketing?.totalWithTax ?? 0
    );
    const applicationFeeWithoutTax = fixMath.add(
        fixMath.add(
            platformFeeBreakdown.platform?.itemTotal ?? 0,
            platformFeeBreakdown.support?.itemTotal ?? 0
        ),
        platformFeeBreakdown.marketing?.itemTotal ?? 0
    );
    const applicationFeeTax = fixMath.add(
        fixMath.add(
            platformFeeBreakdown.platform?.tax ?? 0,
            platformFeeBreakdown.support?.tax ?? 0
        ),
        platformFeeBreakdown.marketing?.tax ?? 0
    );

    return {
        buyerPays: roundDecimalPlaces(buyerTotal, roundCount),
        tax: roundDecimalPlaces(totalTax, roundCount),
        storeRevenue: roundDecimalPlaces(netStoreRevenue, roundCount),
        allTransparencyBreakdown,
        buyerPaysTaxAndFeesName,
        buyerPaysTaxAndFees,
        buyerPaysTaxAndFeesShownFee: roundDecimalPlaces(buyerPaysTaxAndFeesShownFee, roundCount),
        discount: roundDecimalPlaces(discount, roundCount),
        firstOrdersDiscount: roundDecimalPlaces(firstOrdersDiscount, roundCount),
        buyerPaysAfterCreditPromo: roundDecimalPlaces(buyerPaysAfterCreditPromo, roundCount),
        usedCredit: roundDecimalPlaces(usedCredit, roundCount),
        platformFeeBreakdown, // we need this for saving order data
        applicationFeeWithTax: roundDecimalPlaces(applicationFeeWithTax, roundCount),
        applicationFeeWithoutTax: roundDecimalPlaces(applicationFeeWithoutTax, roundCount),
        applicationFeeTax: roundDecimalPlaces(applicationFeeTax, roundCount),
        earnCreditAmountByOrdering: roundDecimalPlaces(earnCreditAmountByOrdering, roundCount),
        // storeBillingAddressSalesTaxRate: storeBillingAddressSalesTaxRate,
    };
}

const MAX_PLATFORM_PARTNER_FEE_RATE = 5;
const MAX_SUPPORT_PARTNER_FEE_RATE = 1;
const MAX_MARKETING_PARTNER_FEE_RATE = 10;


function getLimitedPartnerFeeAmount(
    subTotalWithTax: number,
    fee: ServiceFee,
    maxRate: number
): number {
    const uncapped = fixMath.add(
        exclusiveRate(subTotalWithTax, fee.ratePerOrder ?? 0),
        fee.feePerOrder ?? 0
    );
    const maxAllowed = exclusiveRate(subTotalWithTax, maxRate);
    return Math.min(uncapped, maxAllowed);
    // return uncapped;
}

// function getLimitedPartnerFeeAmount(
//     subTotalWithTax: number,
//     fee: ServiceFee,
//     maxRate: number
// ): number {
//     const uncapped = fixMath.add(
//         exclusiveRate(subTotalWithTax, fee.ratePerOrder ?? 0),
//         fee.feePerOrder ?? 0
//     );
//     const maxAllowed = exclusiveRate(subTotalWithTax, maxRate);
//     return Math.min(uncapped, maxAllowed);
// }

// // type: 'tax' | 'platform' | 'support' | 'marketing' | 'store',
export const feeBreakdownNameMap = {
    tax: {
        name: 'TAX',
        note: '',
    },
    serviceFee: {
        name: 'Platform support fee',
        // note: 'A small service fee helps you get the best offers from sellers. It covers payment and order processing, referral, support, technical services like hosting, api.',
        note: 'This small fee helps us maintain and improve the platform, process your order securely and provide customer support.',
    },
    support: {
        name: 'Support',
        note: 'A local team supporting this seller',
    },
    marketing: {
        name: 'Referral',
        note: 'A local team helping this website grow',
    },
    seller: {
        name: 'Seller',
        note: '',
    },
    platform: {
        name: 'Hosting, SMS, software etc.',
        note: 'Zemega',
    },
};

